import type { Design, WristState } from './model';

export type FitPolicy = { version: string; status: 'ESTIMATED'; data_status: 'TEST'; basis: string; pi_millionths: number; almost_um: number; max_component_um: number; ease_um: Record<string, [number, number]> };
export type FitComponent = { effective_length_um: number | null; radial_diameter_um: number | null; length_source: string; diameter_source: string };
export type FitInputs = { policy: FitPolicy; components: Record<string, FitComponent> };
export type FitStatus = 'FIT_DATA_PENDING' | 'TOO_SHORT' | 'ALMOST_THERE' | 'ESTIMATED_GOOD_FIT' | 'TOO_LONG';
export const FIT_CATALOG: { inputs: FitInputs | null } = { inputs: null };

export function configureFitInputs(value: unknown) {
  FIT_CATALOG.inputs = null;
  if (!value || typeof value !== 'object') return;
  const inputs = value as FitInputs;
  const p = inputs.policy;
  if (!p || typeof p.version !== 'string' || p.status !== 'ESTIMATED' || p.data_status !== 'TEST'
    || !Number.isSafeInteger(p.pi_millionths) || p.pi_millionths < 0 || p.pi_millionths > 4000000
    || !Number.isSafeInteger(p.almost_um) || p.almost_um < 0
    || !Number.isSafeInteger(p.max_component_um) || p.max_component_um <= 0 || p.max_component_um > 100000
    || !p.ease_um || !['snug', 'comfort', 'loose'].every(key => Array.isArray(p.ease_um[key]) && p.ease_um[key].length === 2
      && p.ease_um[key].every(n => Number.isSafeInteger(n) && n >= 0 && n <= 100000) && p.ease_um[key][0] <= p.ease_um[key][1])
    || !inputs.components || typeof inputs.components !== 'object' || Array.isArray(inputs.components)) return;
  FIT_CATALOG.inputs = inputs;
}

export function calculateFit(design: Design, wrist: WristState | null, inputs: FitInputs) {
  const policy = inputs.policy;
  let known = 0, weighted = 0;
  const missing: Array<{ uid: string; component_id: string; reason: string }> = [];
  const used: Array<FitComponent & { uid: string; component_id: string }> = [];
  for (const piece of design.inline_items) {
    const c = inputs.components[piece.component_id];
    const length = c?.effective_length_um;
    const diameter = c?.radial_diameter_um;
    const validLength = typeof length === 'number' && Number.isSafeInteger(length) && length > 0 && length <= policy.max_component_um;
    const validDiameter = typeof diameter === 'number' && Number.isSafeInteger(diameter) && diameter > 0 && diameter <= policy.max_component_um;
    if (validLength) known += length;
    if (validLength && validDiameter) weighted += length * diameter;
    else missing.push({ uid: piece.uid, component_id: piece.component_id, reason: validLength ? 'diameter_pending' : 'length_pending' });
    used.push({ uid: piece.uid, component_id: piece.component_id, effective_length_um: validLength ? length : null,
      radial_diameter_um: validDiameter ? diameter : null, length_source: c?.length_source ?? 'pending', diameter_source: c?.diameter_source ?? 'pending' });
  }
  const preference = design.fit_preference;
  const wristUm = wrist?.confirmed && Number.isSafeInteger(wrist.normalized_um) && wrist.normalized_um > 0 ? wrist.normalized_um : null;
  const ready = wristUm !== null && Boolean(policy.ease_um[preference ?? '']) && !missing.length && known > 0;
  let min: number | null = null, max: number | null = null, correction: number | null = null, difference: number | null = null;
  let status: FitStatus = 'FIT_DATA_PENDING';
  if (ready) {
    const meanDiameter = Math.floor(weighted / known + 0.5);
    correction = Math.floor(meanDiameter * policy.pi_millionths / 1000000 + 0.5);
    min = wristUm! + correction + policy.ease_um[preference!][0];
    max = wristUm! + correction + policy.ease_um[preference!][1];
    difference = known < min ? known - min : known > max ? known - max : 0;
    status = difference < 0 ? (-difference <= policy.almost_um ? 'ALMOST_THERE' : 'TOO_SHORT') : difference > 0 ? 'TOO_LONG' : 'ESTIMATED_GOOD_FIT';
  }
  return {
    customer_wrist_mm: wristUm === null ? null : wristUm / 1000, input_unit: design.wrist?.unit ?? null, fit_preference: preference,
    current_effective_length_mm: missing.length ? null : known / 1000, known_length_um: known,
    target_fit_min_mm: min === null ? null : min / 1000, target_fit_max_mm: max === null ? null : max / 1000,
    difference_mm: difference === null ? null : difference / 1000, difference_um: difference,
    diameter_correction_um: correction, fit_status: status, fit_model_version: policy.version, fit_model_status: 'ESTIMATED',
    parameters: policy, components: used, missing_components: missing,
    attachment_policy: 'hanging_attachments_excluded_inline_connectors_counted', confirmation_allowed: ready, range_policy: 'advisory_not_production_limits',
  };
}

export function currentFit(design: Design, wrist: WristState | null) {
  return FIT_CATALOG.inputs ? calculateFit(design, wrist, FIT_CATALOG.inputs) : null;
}

// Recompute after each candidate change: adding/removing a size also changes the diameter correction.
export function fitSuggestions(design: Design, wrist: WristState | null, candidates: Array<{ id: string; label: string; available: number }>) {
  const inputs = FIT_CATALOG.inputs, current = currentFit(design, wrist);
  if (!inputs || !current?.difference_um || !current.confirmation_allowed) return [];
  const suggestions: Array<{ text: string; distance: number }> = [];
  const counts = new Map<string, number>();
  for (const item of design.inline_items) counts.set(item.component_id, (counts.get(item.component_id) ?? 0) + 1);
  for (const c of candidates) {
    const adding = current.difference_um < 0;
    const limit = adding ? Math.min(c.available - (counts.get(c.id) ?? 0), 256 - design.inline_items.length, 3) : Math.min(counts.get(c.id) ?? 0, 3);
    for (let count = 1; count <= limit; count++) {
      let removed = 0;
      const inline = adding ? [...design.inline_items, ...Array.from({ length: count }, (_, i) => ({ uid: `fit-suggestion-${i}`, component_id: c.id, orientation_deg: 0 }))]
        : design.inline_items.filter(item => item.component_id !== c.id || ++removed > count);
      // Never suggest breaking a hanging attachment's anchor.
      if (design.attachments.some(item => !inline.some(piece => piece.uid === item.anchor_uid))) continue;
      const next = calculateFit({ ...design, inline_items: inline }, wrist, inputs);
      if (next.difference_um !== null && Math.abs(next.difference_um) < Math.abs(current.difference_um)) {
        suggestions.push({ text: `${adding ? 'Add' : 'Remove'} ${count} × ${c.label}`, distance: Math.abs(next.difference_um) });
      }
    }
  }
  return suggestions.sort((a, b) => a.distance - b.distance || a.text.localeCompare(b.text)).slice(0, 2).map(s => s.text);
}
